"use client";

import styled from "styled-components";
import { Icon } from "../Icon/Icon";
import PainIcon from "@/assets/icons/HighPainLevel.svg";
import IncisionIcon from "@/assets/icons/IncisionPhoto.svg";
import CloseIcon from "@/assets/icons/CloseIcon.svg";
import theme from "@/themes/velys-theme";
import { useTranslations } from "next-intl";
import moment from "moment";
import { INotification } from "../testData/orgNotifications";
import { useBoundStore } from "@/lib/stores/useBoundStore";
import { useRouter, usePathname } from "next/navigation";
import { MetricsLoading } from "../Loading/MetricsLoading";
import CaseDataLoader from "../CaseDataLoader/CaseDataLoader";

type NotificationItemProps = {
  notif: INotification;
  patientName: string;
  handleDismissNotif: (id: string) => void;
  enrollmentId: string;
  isLoadingName?: boolean;
  isPending: boolean;
};

const NotificationItemWrapper = styled.div`
  padding: 8px 0px;
  border-bottom: 1px solid #eee;
  cursor: pointer;

  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;

    p {
      span {
        color: ${(props) => props.theme.blue};
        font-weight: bold;
      }
    }

    .notification-text {
      display: flex;
      align-items: flex-start;
      font-size: 0.8rem;
      gap: 5px;

      .icon {
        margin-top: 3px;
      }

      &.time-ago {
        text-align: right;
        white-space: nowrap;
        align-self: flex-start;
      }
    }
  }
`;

export function NotificationItem({
  notif,
  patientName,
  handleDismissNotif,
  enrollmentId,
  isLoadingName = false,
  isPending,
}: NotificationItemProps) {
  const tNotifications = useTranslations("notifications");
  const router = useRouter();
  const pathname = usePathname();
  const setOpenNotifications = useBoundStore(
    (state) => state.setOpenNotifications
  );
  const setOpenAllNotifs = useBoundStore((state) => state.setOpenAllNotifs);

  const timeAgo = (timestamp: string): string => {
    const now = moment();
    const past = moment(timestamp);
    const diffHours = now.diff(past, "hours");
    const diffDays = now.diff(past, "days");
    const diffMinutes = now.diff(past, "minutes");

    if (diffDays > 0) {
      return diffDays === 1
        ? `1 ${tNotifications("dayAgo")}`
        : `${diffDays} ${tNotifications("daysAgo")}`;
    } else if (diffHours > 0) {
      return diffHours === 1
        ? `1 ${tNotifications("hrAgo")}`
        : `${diffHours} ${tNotifications("hrsAgo")}`;
    } else if (diffMinutes > 0) {
      return diffMinutes === 1
        ? `1 ${tNotifications("minAgo")}`
        : `${diffMinutes} ${tNotifications("minsAgo")}`;
    } else {
      return tNotifications("justNow");
    }
  };

  const selectPatient = () => {
    setOpenNotifications(false);
    setOpenAllNotifs(false);

    const basePath = pathname.match(/^(.*\/provider)\//)?.[1] || pathname;
    router.push(
      `${basePath}/patientpath/enrollments/${enrollmentId}/${
        notif.notification_slug.includes("PHOTO_UPLOADED") ? "info" : "metrics"
      }`
    );
  };

  const getNotificationText = () => {
    if (notif.notification_slug === "PHOTO_UPLOADED") {
      return tNotifications("postText");
    } else if (notif.notification_slug === "PRE_OP_PHOTO_UPLOADED") {
      return tNotifications("preText");
    } else if (notif.notification_slug === "ASSESSMENT_PAIN") {
      return tNotifications("painText");
    } else {
      return tNotifications("incisionText");
    }
  };

  return (
    <NotificationItemWrapper
      key={notif.id}
      onClick={enrollmentId !== "" ? selectPatient : () => {}}
    >
      <div className="row">
        <p>
          <span>
            <MetricsLoading
              loading={isLoadingName}
              value={`${patientName}:`}
              width="50px"
              height={"0.9rem"}
            />
          </span>{" "}
          {tNotifications("newActivity")}
        </p>
        {isPending ? (
          <CaseDataLoader percent={null} size="15px" />
        ) : (
          <Icon
            customClass="dismiss-notif"
            url={CloseIcon.src}
            color={theme.black}
            height="15px"
            width="15px"
            onPress={(e) => {
              e.stopPropagation();
              handleDismissNotif(notif.id);
            }}
          />
        )}
      </div>

      <div className="row">
        <div className="notification-text">
          {notif.notification_slug.includes("PHOTO_UPLOADED") ? (
            <Icon
              customClass="incision-photo icon"
              url={IncisionIcon.src}
              color={theme.main}
              height="15px"
              width="15px"
            />
          ) : (
            <Icon
              customClass="high-pain icon"
              url={PainIcon.src}
              color={theme.main}
              height="15px"
              width="15px"
            />
          )}
          <span>{getNotificationText()}</span>
        </div>
        <p className="notification-text time-ago">{timeAgo(notif.timestamp)}</p>
      </div>
    </NotificationItemWrapper>
  );
}
